import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Copy, Check, Trash2, Loader2, Play, Save, ImageIcon, Tag } from 'lucide-react';
import DOMPurify from 'dompurify';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { useToast } from '../components/ui/Toast.tsx';
import { type MediaAsset, formatBytes, thumbUrl, getDocumentPreviewType, isEmbed, isDocument } from '../lib/media.ts';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtDuration(secs: number): string {
  const totalSecs = Math.floor(secs);
  const m = Math.floor(totalSecs / 60);
  const s = totalSecs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function StatusBadge({ status }: { status?: string | null }) {
  const map: Record<string, string> = {
    done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    processing: 'bg-blue-50 text-blue-700 border-blue-200',
    failed: 'bg-red-50 text-red-700 border-red-200',
    unsupported: 'bg-zinc-100 text-zinc-500 border-zinc-200',
  };
  const cls = map[status ?? ''] ?? 'bg-zinc-100 text-zinc-500 border-zinc-200';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full border ${cls}`}
    >
      {status === 'pending' && <Loader2 size={10} className="animate-spin" />}
      {status ?? 'unknown'}
    </span>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-zinc-100">
        {icon && <span className="text-zinc-400">{icon}</span>}
        <h2 className="text-sm font-semibold text-zinc-700">{title}</h2>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-zinc-50 last:border-0">
      <span className="text-xs text-zinc-500 shrink-0">{label}</span>
      <span className="text-xs text-zinc-800 font-medium text-right break-all">{value ?? '—'}</span>
    </div>
  );
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-lg hover:bg-zinc-100 transition-colors"
    >
      {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
      {label ?? (copied ? 'Copied!' : 'Copy URL')}
    </button>
  );
}

// ─── Tags input ───────────────────────────────────────────────────────────────

function TagsInput({ value, onChange }: { value: string[]; onChange: (tags: string[]) => void }) {
  const [inputVal, setInputVal] = useState('');

  const addTag = (raw: string) => {
    const tag = raw.trim();
    if (!tag || value.includes(tag)) {
      setInputVal('');
      return;
    }
    onChange([...value, tag]);
    setInputVal('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(inputVal);
    } else if (e.key === 'Backspace' && inputVal === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div className="flex flex-wrap gap-1.5 min-h-[38px] w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 focus-within:ring-2 focus-within:ring-zinc-900 focus-within:border-zinc-900 transition-colors">
      {value.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-100 text-zinc-700 text-xs rounded-full"
        >
          {tag}
          <button
            type="button"
            onClick={() => onChange(value.filter((t) => t !== tag))}
            className="text-zinc-400 hover:text-zinc-700"
          >
            ×
          </button>
        </span>
      ))}
      <input
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (inputVal.trim()) addTag(inputVal);
        }}
        placeholder={value.length === 0 ? 'Add tags (Enter or comma to add)' : ''}
        className="flex-1 min-w-[120px] outline-none text-xs text-zinc-800 bg-transparent placeholder-zinc-400"
      />
    </div>
  );
}

// ─── Thumbnail strip ──────────────────────────────────────────────────────────

function ThumbnailStrip({ asset }: { asset: MediaAsset }) {
  const thumbs = asset.thumbnails;
  if (!thumbs) return null;

  const sizes: Array<{ key: keyof typeof thumbs; label: string }> = [
    { key: 'w100', label: '100px' },
    { key: 'w300', label: '300px' },
    { key: 'w600', label: '600px' },
    { key: 'w1200', label: '1200px' },
  ];

  const available = sizes.filter((s) => !!thumbs[s.key]);
  if (available.length === 0) return null;

  return (
    <div className="space-y-2">
      {available.map(({ key, label }) => {
        const url = thumbs[key]!;
        return (
          <div
            key={key}
            className="flex items-center gap-3 p-2 rounded-lg border border-zinc-100 bg-zinc-50"
          >
            <img
              src={url}
              alt={`${label} thumbnail`}
              className="w-10 h-10 object-cover rounded border border-zinc-200 shrink-0"
            />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-zinc-700">{label}</p>
              <p className="text-[10px] text-zinc-400 truncate">{url}</p>
            </div>
            <CopyButton text={url} label="Copy" />
          </div>
        );
      })}
    </div>
  );
}

// ─── CsvPreview ───────────────────────────────────────────────────────────────

function CsvPreview({ url }: { url: string }) {
  const [rows, setRows] = useState<string[][]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch(url)
      .then((r) => r.text())
      .then((text) => {
        const parsed = text
          .trim()
          .split('\n')
          .slice(0, 100)
          .map((line) => line.split(','));
        setRows(parsed);
      })
      .catch(() => setError('Impossibile caricare il file CSV'));
  }, [url]);

  if (error) return <p className="text-red-400 text-sm">{error}</p>;
  if (!rows.length) return <p className="text-gray-400 text-sm">Caricamento...</p>;

  return (
    <div className="overflow-auto max-h-72 border border-gray-700 rounded text-xs">
      <table className="w-full">
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i === 0 ? 'bg-gray-800 font-semibold' : 'border-t border-gray-800'}>
              {row.map((cell, j) => (
                <td key={j} className="px-2 py-1 border-r border-gray-800 whitespace-nowrap">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── EmbedPlayer ─────────────────────────────────────────────────────────────
// Renders embed HTML after double sanitization:
//   1. Server-side via sanitize-html (strip scripts/events at ingest)
//   2. Client-side via DOMPurify before DOM insertion
// The ref callback assigns to the container's content property after purification.

function EmbedPlayer({ html }: { html: string }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      const purified = DOMPurify.sanitize(html);
      const parser = new DOMParser();
      const doc = parser.parseFromString(purified, 'text/html');
      containerRef.current.replaceChildren(...Array.from(doc.body.childNodes));
    }
  }, [html]);

  return <div ref={containerRef} className="w-full aspect-video rounded overflow-hidden bg-black" />;
}

// ─── MediaDetailPage ──────────────────────────────────────────────────────────

export function MediaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const {
    data: asset,
    isLoading,
    isError,
  } = useQuery<MediaAsset>({
    queryKey: ['media-asset', id],
    queryFn: () => api.get(`/api/v1/media/${id}`).then((r) => r.data),
    enabled: !!id,
    // Continue polling while thumbnails are processing
    refetchInterval: (query) => {
      const a = query.state.data as MediaAsset | undefined;
      return a?.thumbnailStatus === 'pending' ? 5_000 : false;
    },
  });

  // ── Form state ───────────────────────────────────────────────────────────────
  const [altText, setAltText] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);

  // Sync form from server data (only on first load / reset)
  useEffect(() => {
    if (asset && !dirty) {
      setAltText(asset.altText ?? '');
      setTitle(asset.title ?? '');
      setDescription(asset.description ?? '');
      setTags(asset.tags ?? []);
    }
  }, [asset, dirty]);

  const markDirty = () => setDirty(true);

  // ── Save mutation ────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: () =>
      api
        .patch(`/api/v1/media/${id}`, {
          altText: altText || null,
          title: title || null,
          description: description || null,
          tags,
        })
        .then((r) => r.data as MediaAsset),
    onSuccess: (updated) => {
      qc.setQueryData(['media-asset', id], updated);
      qc.invalidateQueries({ queryKey: ['media-assets'] });
      setDirty(false);
      toast.success('Metadata saved');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  // ── Delete mutation ──────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/api/v1/media/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['media-assets'] });
      toast.success('Asset deleted');
      navigate('/media');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const handleDelete = () => {
    if (!asset) return;
    if (!confirm(`Delete "${asset.fileName}"? This cannot be undone.`)) return;
    deleteMutation.mutate();
  };

  // ── Loading / error states ───────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto flex items-center justify-center py-32 text-zinc-400">
        <Loader2 size={28} className="animate-spin mr-3" />
        Loading asset…
      </div>
    );
  }

  if (isError || !asset) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <button
          onClick={() => navigate('/media')}
          className="flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-6"
        >
          <ArrowLeft size={15} /> Back to Media Library
        </button>
        <div className="text-center py-24 text-red-500 text-sm">
          Asset not found or failed to load.
        </div>
      </div>
    );
  }

  const isVideo = asset.assetType === 'video';
  const previewUrl = thumbUrl(asset) ?? undefined;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-4">
        <button
          onClick={() => navigate('/media')}
          className="mt-0.5 p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
          title="Back to Media Library"
        >
          <ArrowLeft size={18} />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold text-zinc-900 truncate">{asset.fileName}</h1>
            <StatusBadge status={asset.thumbnailStatus} />
          </div>
          <p className="text-sm text-zinc-400 mt-0.5">
            {asset.mimeType} · {formatBytes(asset.sizeBytes)}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <CopyButton text={asset.publicUrl ?? ''} />
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
          >
            {deleteMutation.isPending ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Trash2 size={12} />
            )}
            Delete
          </button>
        </div>
      </div>

      {/* ── Two-column layout ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ── Left column: preview + details ────────────────────────────── */}
        <div className="space-y-5">
          {/* Preview */}
          <div className="bg-zinc-100 rounded-xl overflow-hidden flex items-center justify-center aspect-square relative">
            {isVideo ? (
              <>
                <img src={previewUrl} alt={asset.fileName} className="w-full h-full object-cover" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 rounded-full bg-black/60 flex items-center justify-center">
                    <Play size={22} className="text-white ml-1" fill="white" />
                  </div>
                </div>
              </>
            ) : (
              <img src={previewUrl} alt={asset.fileName} className="w-full h-full object-contain" />
            )}
          </div>

          {/* KV details */}
          <Section title="Details" icon={<ImageIcon size={14} />}>
            <KV label="ID" value={<span className="font-mono text-[10px]">{asset.id}</span>} />
            <KV label="Type" value={asset.assetType ?? '—'} />
            <KV label="MIME" value={asset.mimeType} />
            <KV label="Size" value={formatBytes(asset.sizeBytes)} />
            {asset.width && asset.height && (
              <KV label="Dimensions" value={`${asset.width} × ${asset.height} px`} />
            )}
            {asset.duration != null && <KV label="Duration" value={fmtDuration(asset.duration)} />}
            <KV label="Uploaded" value={fmtDate(asset.createdAt)} />
            <KV label="Thumbnail status" value={<StatusBadge status={asset.thumbnailStatus} />} />
          </Section>
        </div>

        {/* ── Right column: metadata edit + thumbnails ───────────────────── */}
        <div className="lg:col-span-2 space-y-5">
          {/* Metadata form */}
          <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-100">
              <h2 className="text-sm font-semibold text-zinc-700">Metadata</h2>
              {dirty && (
                <span className="text-[11px] text-amber-600 font-medium">Unsaved changes</span>
              )}
            </div>
            <div className="p-5 space-y-4">
              {/* Alt text */}
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1.5">Alt text</label>
                <input
                  type="text"
                  value={altText}
                  onChange={(e) => {
                    setAltText(e.target.value);
                    markDirty();
                  }}
                  placeholder="Describe the image for screen readers"
                  className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-zinc-900 transition-colors"
                />
              </div>

              {/* Title */}
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1.5">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    markDirty();
                  }}
                  placeholder="Human-readable asset title"
                  className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-zinc-900 transition-colors"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1.5">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    markDirty();
                  }}
                  placeholder="Optional longer description"
                  rows={3}
                  className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-zinc-900 transition-colors resize-none"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-xs font-medium text-zinc-600 mb-1.5">Tags</label>
                <TagsInput
                  value={tags}
                  onChange={(t) => {
                    setTags(t);
                    markDirty();
                  }}
                />
              </div>

              {/* Save button */}
              <div className="pt-1">
                <button
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending || !dirty}
                  className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saveMutation.isPending ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Save size={14} />
                  )}
                  {saveMutation.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          {/* Document preview */}
          {isDocument(asset) && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-400 mb-2">Anteprima documento</h3>
              {(() => {
                const previewType = getDocumentPreviewType(asset.mimeType ?? null);
                if (previewType === 'pdf' && asset.publicUrl) {
                  return (
                    <iframe
                      src={asset.publicUrl}
                      className="w-full border border-gray-700 rounded"
                      style={{ height: 500 }}
                      title={asset.fileName}
                    />
                  );
                }
                if (previewType === 'office' && asset.publicUrl) {
                  return (
                    <iframe
                      src={`https://docs.google.com/viewer?url=${encodeURIComponent(asset.publicUrl)}&embedded=true`}
                      className="w-full border border-gray-700 rounded"
                      style={{ height: 500 }}
                      title={asset.fileName}
                    />
                  );
                }
                if (previewType === 'csv' && asset.publicUrl) {
                  return <CsvPreview url={asset.publicUrl} />;
                }
                return (
                  <div className="flex items-center gap-3 p-4 bg-gray-800 rounded">
                    <span className="text-3xl">🗜</span>
                    <a
                      href={asset.publicUrl ?? '#'}
                      download={asset.fileName}
                      className="text-indigo-400 hover:underline text-sm"
                    >
                      ⬇ Download {asset.fileName}
                    </a>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Embed player — embedHtml is rendered via DOMPurify-sanitized ref assignment */}
          {isEmbed(asset) && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-400 mb-2">Embed player</h3>
              {asset.embedHtml ? (
                <EmbedPlayer html={asset.embedHtml} />
              ) : (
                <p className="text-gray-400 text-sm">Nessun codice embed disponibile</p>
              )}
              <div className="flex gap-2 mt-2">
                {asset.embedUrl && (
                  <a
                    href={asset.embedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-400 hover:underline text-sm"
                  >
                    🔗 Apri originale
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Thumbnails */}
          {!isEmbed(asset) && asset.thumbnails && (
            <Section title="Thumbnails" icon={<Tag size={14} />}>
              <ThumbnailStrip asset={asset} />
            </Section>
          )}

          {/* Public URL */}
          <Section title="Public URL" icon={<Copy size={14} />}>
            <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg border border-zinc-100">
              <p className="flex-1 text-xs text-zinc-600 font-mono break-all">{asset.publicUrl ?? '—'}</p>
              <CopyButton text={asset.publicUrl ?? ''} />
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
